import * as CSSTransformParser from 'transform-parser';

import { isFunction } from 'shared/lib/utils';

import { setUpDragableElements } from '@/lib/renderers/dragAndDrop';

export default class YAxisLabelsRenderer {
  labelGroupElsByOriginalName: SVGGElement[];
  labelTextEls: SVGGElement[];
  labelTextElsByOriginalName: Map<string, SVGGElement>;
  labelBBoxesByOriginalName;
  yAxesByOriginalName;
  yAxes;

  _svgEl;

  render({
    el,
    yAxes,
    labelGroupElsByOriginalName,
    shouldSetUpDraggableLabels,
    onLabelDragStart,
    onLabelDragEnd,
    updateYAxisByAttributes,
  }) {
    this._svgEl = el.querySelector('svg');
    this.yAxes = yAxes;

    this.yAxesByOriginalName = this.yAxes.reduce(
      (memo, yAxis) => memo.set(yAxis.get('name'), yAxis),
      new Map()
    );

    // Selecting elements rendered by Vega lite //////////////////////////////////

    this.labelTextElsByOriginalName = new Map(
      Array.from(
        labelGroupElsByOriginalName,
        ([labelOriginalName, labelGroupEl]) => [
          labelOriginalName,
          labelGroupEl.querySelector('text'),
        ]
      )
    );

    this.labelBBoxesByOriginalName = new Map(
      Array.from(
        this.labelTextElsByOriginalName,
        ([labelOriginalName, labelTextEl]) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const { yAxis } = labelTextEl.__data__.datum.fieldLabel;
          const userDefinedPosition = yAxis.style.labelPosition;

          const transform = labelTextEl.getAttribute('transform');
          const parsedTransform = transform
            ? CSSTransformParser.parse(transform)
            : null;
          const vegaX = parsedTransform?.translate?.[0];
          const vegaY = parsedTransform?.translate?.[1];

          let x;
          let y;

          if (userDefinedPosition) {
            x = userDefinedPosition[0];
            y = userDefinedPosition[1];
          } else {
            x = vegaX;
            y = vegaY;
          }

          const { width, height } = labelTextEl.getBBox();

          return [
            labelOriginalName,
            {
              vegaX,
              vegaY,
              x,
              y,
              width,
              height,
            },
          ];
        }
      )
    );

    this.labelBBoxesByOriginalName.forEach((labelBBox, originalName) => {
      const labelTextEl = this.labelTextElsByOriginalName.get(originalName);

      if (!labelTextEl) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { yAxis } = labelTextEl.__data__.datum.fieldLabel;
      labelTextEl.setAttribute('font-size', yAxis.style.labelFontSize);

      const { x, y } = labelBBox;
      labelTextEl.setAttribute('transform', `translate(${x}, ${y})`);
    });

    if (shouldSetUpDraggableLabels) {
      setUpDragableElements({
        svgEl: this._svgEl,
        elementsById: this.labelTextElsByOriginalName,
        elementBBoxesById: this.labelBBoxesByOriginalName,
        onElementDragStart: onLabelDragStart,
        onElementDrag: (yAxisOriginalName, [x, y]) => {
          const labelTextEl =
            this.labelTextElsByOriginalName.get(yAxisOriginalName);

          if (!labelTextEl) {
            return null;
          }

          labelTextEl.setAttribute('transform', `translate(${x}, ${y})`);
        },
        onElementDragEnd: (yAxisOriginalName, positionAtDragEng) => {
          updateYAxisByAttributes(
            this.yAxesByOriginalName.get(yAxisOriginalName),
            {
              style_label_position: positionAtDragEng,
            }
          );

          isFunction(onLabelDragEnd) && onLabelDragEnd();
        },
      });
    }
  }

  automaticallyRearrangeLabels() {
    this.labelBBoxesByOriginalName.forEach((labelBBox, originalName) => {
      const labelTextEl = this.labelTextElsByOriginalName.get(originalName);

      if (!labelTextEl) {
        return;
      }

      const { vegaX, vegaY } = labelBBox;
      labelTextEl.setAttribute('transform', `translate(${vegaX}, ${vegaY})`);
    });

    this.labelBBoxesByOriginalName.forEach((labelBBox) => {
      labelBBox.x = labelBBox.vegaX;
      labelBBox.y = labelBBox.vegaY;
    });

    return this.labelBBoxesByOriginalName;
  }
}
